/**
 * LiquidGlass Component
 *
 * Apple visionOS-inspired glassmorphism surface with fluid animations.
 * Provides translucent glass surfaces with specular highlights and
 * spring-based morphing animations.
 *
 * @example
 * ```tsx
 * import { LiquidGlass } from '@/ui';
 *
 * // Basic glass surface
 * <LiquidGlass>
 *   <Content />
 * </LiquidGlass>
 *
 * // Premium glass with specular highlight and animation
 * <LiquidGlass thickness="thick" specular animate="appear" rounded="xl">
 *   <Heading>Premium Content</Heading>
 *   <Text>Beautiful glass surface with specular highlight.</Text>
 * </LiquidGlass>
 *
 * // Interactive glass card
 * <LiquidGlass interactive thickness="regular" rounded="lg">
 *   <CardContent />
 * </LiquidGlass>
 * ```
 *
 * @see docs/frontend/liquid-glass.md for complete documentation
 */

import * as React from 'react';
import type {VariantProps} from 'class-variance-authority';
import {cn} from '@/shared/utils/utils';
import {liquidGlassVariants} from './LiquidGlass.variants';

// =============================================================================
// TYPES
// =============================================================================

interface LiquidGlassProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof liquidGlassVariants> {
  /**
   * The HTML element to render as
   * @default 'div'
   */
  as?: 'div' | 'section' | 'article' | 'aside' | 'header' | 'footer' | 'nav';
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * LiquidGlass - Apple visionOS-inspired glassmorphism surface
 *
 * Use LiquidGlass for premium translucent surfaces with depth.
 * This component provides:
 * - Translucent glass materials (thin, regular, thick, ultra)
 * - Specular highlights (Apple-style top shine)
 * - Fluid morph animations
 * - Spring-based timing
 * - Full dark mode support
 * - Reduced motion accessibility
 */
export function LiquidGlass({
  as: Component = 'div',
  thickness,
  rounded,
  padding,
  interactive,
  specular,
  animate,
  shadow,
  className,
  children,
  ...props
}: LiquidGlassProps): React.ReactElement {
  return (
    <Component
      className={cn(
        liquidGlassVariants({
          thickness,
          rounded,
          padding,
          interactive,
          specular,
          animate,
          shadow,
        }),
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
}

// =============================================================================
// CONVENIENCE COMPONENTS
// =============================================================================

/**
 * LiquidGlassPanel - Less prominent glass effect for panels/sidebars
 */
export function LiquidGlassPanel({
  children,
  className,
  ...props
}: Omit<LiquidGlassProps, 'thickness' | 'shadow'>): React.ReactElement {
  return (
    <LiquidGlass
      thickness="thin"
      shadow="sm"
      className={className}
      {...props}
    >
      {children}
    </LiquidGlass>
  );
}

/**
 * LiquidGlassCard - Interactive glass card with hover effect
 */
export function LiquidGlassCard({
  children,
  className,
  ...props
}: Omit<LiquidGlassProps, 'interactive'>): React.ReactElement {
  return (
    <LiquidGlass
      interactive
      className={className}
      {...props}
    >
      {children}
    </LiquidGlass>
  );
}

/**
 * LiquidGlassHero - Prominent glass for hero sections
 */
export function LiquidGlassHero({
  children,
  className,
  ...props
}: Omit<LiquidGlassProps, 'thickness' | 'padding' | 'specular'>): React.ReactElement {
  return (
    <LiquidGlass
      thickness="thick"
      padding="xl"
      specular
      rounded="xl"
      shadow="lg"
      className={className}
      {...props}
    >
      {children}
    </LiquidGlass>
  );
}

/**
 * LiquidGlassOverlay - Full-screen glass overlay for modals/dialogs
 */
export function LiquidGlassOverlay({
  children,
  className,
  ...props
}: Omit<LiquidGlassProps, 'thickness' | 'rounded'>): React.ReactElement {
  return (
    <LiquidGlass
      thickness="ultra"
      rounded="none"
      animate="appear"
      className={cn('fixed inset-0 z-overlay', className)}
      {...props}
    >
      {children}
    </LiquidGlass>
  );
}

// =============================================================================
// EXPORTS
// =============================================================================

// All components are already exported above via named exports
